import type { StreamErrorDetails } from "@/api/models";
import { getErrorDetails, getErrorSummary } from "@/utils/errorMessages";

export type FileProcessingStatus = {
  fileProcessingComplete: boolean;
  fileProcessingSuccess: boolean;
  res: unknown;
  error?: 'polling' | 'processing';
  errorMessage?: string;
  errorDetails?: StreamErrorDetails;
};

type StatusResponse = {
  ok?: boolean;
  status?: number;
  statusText?: string;
  json: () => Promise<unknown>;
};

type StatusRequest = (statusURL: string) => Promise<StatusResponse | null>;

const pendingStatus = (
  error?: FileProcessingStatus['error'],
  errorMessage?: string,
  errorDetails?: StreamErrorDetails,
): FileProcessingStatus => ({
  fileProcessingComplete: false,
  fileProcessingSuccess: false,
  res: [],
  error,
  ...(errorMessage ? { errorMessage } : {}),
  ...(errorDetails && Object.keys(errorDetails).length > 0 ? { errorDetails } : {}),
});

const extractFailureMessage = (value: unknown): string | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  if (typeof record.error === "string") return record.error;
  if (record.error && typeof record.error === "object" && typeof (record.error as Record<string, unknown>).message === "string") {
    return (record.error as Record<string, string>).message;
  }
  if (typeof record.message === "string") return record.message;
  if (typeof record.output === "string") {
    try {
      return extractFailureMessage(JSON.parse(record.output));
    } catch {
      return undefined;
    }
  }
  return record.output && typeof record.output === "object"
    ? extractFailureMessage(record.output)
    : undefined;
};

const sanitizeStatusDiagnostic = (value?: string): string | undefined => value
  ?.replace(/https?:\/\/[^\s"'<>]+/gi, "[request URL redacted]")
  .replace(/(?:\b(?:GET|POST|PUT|PATCH|DELETE)\s+)?\/(?:runtime|api)\/[^\s"'<>]*/gi, "[request path redacted]")
  .replace(/\?[^\s"'<>]*/g, "?[query redacted]")
  .slice(0, 1200);

const sanitizeStatusDetails = (details?: StreamErrorDetails): StreamErrorDetails | undefined => details
  ? { ...details, upstream_detail: sanitizeStatusDiagnostic(details.upstream_detail) }
  : undefined;

/**
 * Reads a single file-indexing status response. Transport and malformed-response
 * errors are transient here; the polling caller owns retry limits and messaging.
 */
export const checkFileProcessingStatus = async (
  statusURL: string,
  requestStatus: StatusRequest,
): Promise<FileProcessingStatus> => {
  try {
    const response = await requestStatus(statusURL);
    if (!response) return pendingStatus('polling');
    if (response.ok === false) {
      const payload = await response.json().catch(() => null) as Record<string, unknown> | null;
      const rawMessage = sanitizeStatusDiagnostic(extractFailureMessage(payload) || response.statusText || undefined);
      const backendDetails = sanitizeStatusDetails(payload?.error_details as StreamErrorDetails | undefined);
      if (!rawMessage && !backendDetails && !response.status) return pendingStatus('polling');
      const errorDetails = getErrorDetails(rawMessage, backendDetails, response.status);
      const message = rawMessage || response.status || Object.keys(errorDetails).length > 0
        ? getErrorSummary(rawMessage, errorDetails)
        : undefined;
      return pendingStatus('polling', message, errorDetails);
    }

    const payload = await response.json();
    if (!payload || typeof payload !== 'object' || !('res' in payload)) {
      return pendingStatus('polling');
    }

    const responseBody = payload as { res: unknown };
    const result = typeof responseBody.res === 'string'
      ? JSON.parse(responseBody.res)
      : responseBody.res;
    if (!result || typeof result !== 'object' || !('runtimeStatus' in result)) {
      return pendingStatus('polling');
    }

    const runtimeStatus = (result as { runtimeStatus: unknown }).runtimeStatus;
    if (runtimeStatus === 'Completed') {
      return {
        fileProcessingComplete: true,
        fileProcessingSuccess: true,
        res: (result as { output?: unknown }).output,
      };
    }
    if (runtimeStatus === 'Failed' || runtimeStatus === 'Terminated' || runtimeStatus === 'Canceled') {
      const failure = result as Record<string, unknown>;
      const rawMessage = sanitizeStatusDiagnostic(extractFailureMessage(failure));
      const errorDetails = getErrorDetails(rawMessage, sanitizeStatusDetails(failure.error_details as StreamErrorDetails | undefined));
      return {
        fileProcessingComplete: true,
        fileProcessingSuccess: false,
        res: [],
        error: 'processing',
        ...(rawMessage ? { errorMessage: getErrorSummary(rawMessage, errorDetails) } : {}),
        ...(Object.keys(errorDetails).length > 0 ? { errorDetails } : {}),
      };
    }

    if (runtimeStatus === 'Pending' || runtimeStatus === 'Running'
      || runtimeStatus === 'ContinuedAsNew' || runtimeStatus === 'Suspended') {
      return pendingStatus();
    }
    return pendingStatus('polling');
  } catch {
    return pendingStatus('polling');
  }
};

export const MAX_TRANSIENT_STATUS_RETRIES = 3;

/**
 * Polls until indexing completes, fails terminally, or repeated transient
 * status-check errors exhaust the retry budget. Normal pending jobs may run for
 * an arbitrary duration, as they did before; only consecutive poll errors are
 * bounded.
 */
export const pollFileProcessingStatus = async (
  statusURL: string,
  checkStatus: (url: string) => Promise<FileProcessingStatus>,
  wait: (milliseconds: number) => Promise<void>,
  intervals: number[],
  maxInterval: number,
  maxTransientRetries = MAX_TRANSIENT_STATUS_RETRIES,
): Promise<FileProcessingStatus> => {
  let pollCount = 0;
  let consecutiveErrors = 0;
  let finished = false;
  let finalStatus: FileProcessingStatus = pendingStatus();

  while (!finished) {
    const status = await checkStatus(statusURL);
    if (status.error === 'polling') {
      consecutiveErrors++;
      if (consecutiveErrors > maxTransientRetries) {
        finalStatus = {
          fileProcessingComplete: true,
          fileProcessingSuccess: false,
          res: [],
          error: 'polling',
          ...(status.errorMessage ? { errorMessage: status.errorMessage } : {}),
          ...(status.errorDetails ? { errorDetails: status.errorDetails } : {}),
        };
        finished = true;
      }
    } else {
      consecutiveErrors = 0;
    }

    if (status.fileProcessingComplete) {
      finalStatus = status;
      finished = true;
    }

    if (finished) break;

    const delay = intervals[pollCount] ?? maxInterval;
    pollCount++;
    await wait(delay);
  }

  return finalStatus;
};
